import React, { useState } from 'react'; 
import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, MessageSquare, Activity, Settings, 
  FileText, Star, LifeBuoy, LogOut, X, Wallet, Compass, ZoomIn, Radio, MoreVertical, AlertTriangle
} from 'lucide-react';
import { useChatHistory } from '../../hooks/connectivityHooks/useChatHistory'; 
import { useExpertSettings } from '../../hooks/expertDashboardHooks/useExpertSettings'; // 🟢 Hook imported
import Logo from "../../assets/trueAdvisoLogo/trueAdvisologofull.jpeg";
import { SupportContactModal } from '../SupportContactModal'; 

interface ExpertSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  expertName: string;
  expertCode: string;
  profileImage?: string; 
  onLogout: () => void;
  onSettingsClick?: () => void; 
}

const NavItem = ({ icon: Icon, label, to, badge, onClick }: { icon: any, label: string, to: string, badge?: string, onClick?: () => void }) => (
  <NavLink 
    to={to}
    onClick={onClick} 
    className={({ isActive }) => `
      w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all group
      ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
    `}
  >
    {({ isActive }) => (
      <>
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
          <span className="font-medium text-sm">{label}</span>
        </div>
        {badge && (
          <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
            {badge}
          </span>
        )}
      </>
    )}
  </NavLink>
);

export const ExpertSidebar: React.FC<ExpertSidebarProps> = ({ 
  isOpen, onClose, expertName, expertCode, profileImage, onLogout,
}) => {
  const { sessions } = useChatHistory('expert', expertCode);
  const activeChatsCount = sessions.filter(
  ({ status }) => status === 'active',
).length;
  const badgeDisplay = activeChatsCount > 0 ? activeChatsCount.toString() : undefined;

  // 🟢 Access deactivate logic from the hook
  const { deactivateAccount } = useExpertSettings(); 

  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false); 
  const [isMenuOpen, setIsMenuOpen] = useState(false); // 🟢 3-dot dropdown state

  // 🟢 Deactivate function moved here
  const handleDeactivate = () => {
    if (window.confirm("Are you absolutely sure you want to deactivate your account? You will be immediately logged out and your profile will be suspended.")) {
      deactivateAccount();
    }
  };

  return (
    <>
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0
      `}>
        <div className="h-full flex flex-col">
          <div className="pt-8 pb-4 px-6 border-b border-slate-800 flex items-center justify-between relative">
            <img src={Logo} alt="trueAdviso Logo" className="w-48 h-12 object-contain object-left" />
            <button onClick={onClose} className="md:hidden text-slate-400 absolute right-4 top-8">
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            <NavItem icon={LayoutDashboard} label="Dashboard" to="/expert-dashboard/dashboard" />
            <NavItem icon={Wallet} label="Wallet" to="/expert-dashboard/wallet" />
            {/* 🟢 NEW ROUTE SELECTION LINK INSERTION */}
          <NavItem icon={FileText} label="My Orders" to="/expert-dashboard/orders" />
            <NavItem icon={MessageSquare} label="Messages" to="/expert-dashboard/messages" badge={badgeDisplay} />
            
            <NavItem icon={Radio} label="Go Live" to="/expert-dashboard/live" />

            <NavItem icon={Activity} label="Activity Logs" to="/expert-dashboard/activity-logs" />
            <NavItem icon={FileText} label="My Blog" to="/expert-dashboard/blog" />
            
            <NavItem icon={Compass} label="Kundli Generator" to="/expert-dashboard/kundli" />

            <NavItem icon={Star} label="Reviews" to="/expert-dashboard/reviews" />
            
            <div className="pt-6 pb-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-4">Support & Settings</p>
            </div>
            
            <NavItem icon={Settings} label="Settings" to="/expert-dashboard/settings" />
            
            <button 
              onClick={() => setIsSupportModalOpen(true)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all group text-slate-400 hover:bg-slate-800 hover:text-white"
            >
              <div className="flex items-center gap-3">
                <LifeBuoy className="w-5 h-5 text-slate-500 group-hover:text-white" />
                <span className="font-medium text-sm">Support Chat</span>
              </div>
            </button>
          </nav>

          <div className="p-4 border-t border-slate-800 bg-slate-950 relative">
            {/* 🟢 Removed overflow-hidden so the dropdown can overlay */}
            <div className="flex items-center gap-3 mb-4 relative overflow-visible">
              {profileImage ? (
                <div className="relative group shrink-0">
                  <img 
                    src={profileImage} 
                    alt={expertName} 
                    className="w-10 h-10 rounded-full object-cover border border-slate-700 cursor-pointer" 
                    onClick={() => setIsPreviewOpen(true)}
                  />
                  <div 
                    className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={() => setIsPreviewOpen(true)}
                  >
                    <ZoomIn className="w-4 h-4 text-white" />
                  </div>
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center text-sm font-bold shrink-0">
                  {expertName[0]}
                </div>
              )}
              
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-white truncate" title={expertName}>
                  {expertName}
                </p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-[9px] font-bold bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded uppercase tracking-wider border border-blue-500/30">
                    Expert
                  </span>
                  <span className="text-[9px] font-bold bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded uppercase tracking-wider border border-slate-700">
                    {expertCode}
                  </span>
                </div>
              </div>

              {/* 🟢 Added 3-dot menu specifically for Deactivation */}
              <div className="relative">
                <button 
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>

                {isMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
                    <div className="absolute bottom-full right-0 mb-2 w-48 bg-slate-800 border border-slate-700 rounded-xl shadow-xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200">
                      <button 
                        onClick={() => {
                          setIsMenuOpen(false);
                          handleDeactivate();
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-slate-700 hover:text-red-300 transition-colors flex items-center gap-2 font-medium"
                      >
                        <AlertTriangle className="w-4 h-4" />
                        Deactivate Account
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <button 
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-2 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white py-2 rounded-lg transition-all text-sm font-medium"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      <SupportContactModal 
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        role="expert"
        name={expertName}
        code={expertCode}
      />

      {isPreviewOpen && profileImage && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 animate-in fade-in duration-200"
          onClick={() => setIsPreviewOpen(false)}
        >
          <div className="relative max-w-4xl w-full flex justify-center animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setIsPreviewOpen(false)}
              className="absolute -top-12 right-0 md:-right-12 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full p-2 transition-all"
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={profileImage} 
              alt="Full Profile Preview" 
              className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </>
  );
};